import React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'
import { useAuth } from '../../../contexts/AuthContext'
import Avatar from '../../../components/Avatar'
import { useTourAnchor } from '../../../contexts/TourAnchorContext'
import { useUnreadMessages } from '../../../hooks/useUnreadMessages'

// Floating liquid-glass tab bar. Active tab becomes a horizontal pill
// with its label inline; the others are icon-only so the bar reads as
// "anchor here" rather than as a row of six equal cells. The active
// pill grows to take the slack the inactive icon buttons leave behind.
//
// `visible` slides the bar off-screen with a transform when false —
// hides on keyboard-open or downward-scroll (decided by
// useBottomBarVisibility in App.jsx). Translation + opacity rather
// than display:none so the bar can flick back in smoothly without
// the layout reflowing.
export function BottomTabBar({ tabs, active, onSelect, visible = true }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const barAnchorRef = useTourAnchor('bottom-bar')
  const profileAnchorRef = useTourAnchor('bottom-bar-profile')
  const unreadMessages = useUnreadMessages()

  if (typeof document === 'undefined') return null

  return createPortal(
    <nav
      className={cn(
        'fixed inset-x-0 z-30 px-3 transition-[transform,opacity] duration-200 ease-out',
        visible ? 'translate-y-0 opacity-100' : 'translate-y-[150%] opacity-0 pointer-events-none',
      )}
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}
      aria-label="Phoenix tabs"
      aria-hidden={visible ? undefined : 'true'}
    >
      <div ref={barAnchorRef} className="relative max-w-screen-sm mx-auto">
        {/* The glass plate itself. backdrop-blur + saturate gives the
            liquid-glass lensing effect over content. Plate opacity +
            border + a thin inner highlight all bumped so the bar
            stays legible when content underneath is very dark (e.g.
            full-bleed dark photos) — at the previous opacities the
            inactive icons effectively disappeared against black. */}
        <div
          className={cn(
            'relative rounded-full overflow-hidden p-1.5',
            'bg-white/60 dark:bg-black/55',
            'backdrop-blur-2xl backdrop-saturate-200',
            'border border-black/10 dark:border-white/20',
            // Inner ring adds a 1px highlight so the edge stays
            // separated from arbitrary backgrounds — independent of
            // the outer border which gets washed out by blur.
            'ring-1 ring-white/30 dark:ring-white/10',
            'shadow-[0_10px_30px_-10px_rgba(0,0,0,0.45)]'
          )}
        >
          {/* Specular highlight — refractive sheen on the top half. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full bg-gradient-to-b from-white/25 to-transparent dark:from-white/10"
          />

          <div className="relative flex items-center gap-1">
            {tabs.map(({ key, icon: Icon, labelKey }) => {
              const isActive = key === active
              // labelKey lets off-nav (ephemeral) tabs supply their own
              // i18n key — Settings, Health profile, Phases, the
              // various workout sub-pages — instead of forcing every
              // route to add a row under phoenixTracker.tabs.
              const label = labelKey ? t(labelKey) : t(`phoenixTracker.tabs.${key}`)
              return (
                <button
                  key={key}
                  type="button"
                  ref={key === 'profile' ? profileAnchorRef : undefined}
                  onClick={() => onSelect(key)}
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={label}
                  className={cn(
                    'flex items-center justify-center h-11 transition-all duration-200',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-500/40 rounded-full',
                    isActive
                      ? 'flex-1 min-w-0 px-3 gap-1.5 bg-app-primary-500/15 text-app-primary-500'
                      // Inactive icons used text-muted-foreground which
                      // sat near-invisible against the more-opaque
                      // glass plate over dark backdrops. The /80 step
                      // keeps them clearly legible without competing
                      // with the active pill.
                      : 'flex-shrink-0 w-11 text-foreground/70 hover:text-foreground hover:bg-foreground/5'
                  )}
                >
                  {/* Profile tab gets the user's avatar in place of
                      the generic User icon — falls back to the icon
                      via Avatar's internal placeholder when no URL
                      is known. Sized larger than the 22px lucide
                      glyphs because a circular photo reads smaller
                      than a bold stroke icon at the same width. */}
                  {key === 'profile' ? (
                    <span className="relative inline-flex">
                      <Avatar userId={user?.id} size={30} className="border-0" />
                      {unreadMessages > 0 && (
                        <span className="absolute -top-1 -end-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold inline-flex items-center justify-center border border-card">
                          {unreadMessages > 9 ? '9+' : unreadMessages}
                        </span>
                      )}
                    </span>
                  ) : (
                    <Icon className="w-[22px] h-[22px] flex-shrink-0" />
                  )}
                  {isActive && (
                    <span className="text-sm font-medium truncate" dir="auto">
                      {label}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </nav>,
    document.body
  )
}
